import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Dataroom } from '@vcip/design-system';
import { exchangeSessionToken, fetchSessionEntrySummary, fetchStartBootstrap } from '@/api';
import type { SessionEntrySummary } from '@/api';
import { LocaleSwitcher } from '@/i18n/LocaleSwitcher';
import { rich, useT } from '@/i18n/runtime';
import { decideEntry, landingCta, mayEnterCall } from './preCallGate';
import { useSessionStore } from '@/store/sessionStore';
import { deriveBrandTokens } from '@/theme/brand';
import { useBrandedDocumentTitle } from '@/theme/documentTitle';
import { parseServedBranding, resolveTenant } from '@/theme/servedTenant';
import type { TenantConfig } from '@/types';
import { AppBar } from './components/AppBar';
import { PoweredByKycHub } from './components/PoweredByKycHub';
import { Button } from './components/Button';
import { Icon } from './components/Icon';
import type { IconName } from './components/Icon';
import { SecurePill } from './components/SecurePill';

/**
 * SESSION LANDING (/session/:sessionId) — the invite link's front door.
 *
 * The calm Dataroom-mode page a customer arrives at from the invite (usually
 * WhatsApp, on a phone): what you'll need, what will happen, and ONE primary
 * action — "Join your video call" → /call/:sessionId, where the geo gate runs
 * before the media join.
 *
 * HONESTY RULES encoded here:
 *   • Session state comes from the backend read (fetchSessionEntrySummary) or
 *     it is not claimed at all — 'unknown' renders a state-agnostic landing,
 *     never an invented "ready"/"ended". Enforcement is server-side at join;
 *     this read only shapes the copy.
 *   • The what-will-happen text says plainly that the call is recorded and
 *     that location + responses become part of the verification record. It is
 *     informational DPDP-adjacent notice — NOT a consent ceremony. The formal
 *     ceremony (S7-7) runs as the flow's consent STEP (via /start, before any
 *     capture) or at the call's ConsentGate; no checkbox is faked here.
 *   • The link itself is the access credential (interim capability-URL model
 *     until customer OTP auth lands with IAM) — the footer says not to share it.
 */

const PREPARING_POLL_MS = 12_000;

export function SessionLanding(): JSX.Element {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const tenantId = useSessionStore((s) => s.session.tenantId);
  const servedBranding = useSessionStore((s) => s.servedBranding);
  const setServedBranding = useSessionStore((s) => s.setServedBranding);
  const setPackLocales = useSessionStore((s) => s.setPackLocales);
  // Item 4 pin: served branding wins; the local fallback registry is fallback only.
  const sessionRegime = useSessionStore((s) => s.session.regime);
  const tenant = resolveTenant(tenantId, servedBranding, sessionRegime);

  // null = the first read is still in flight ("checking").
  const [summary, setSummary] = useState<SessionEntrySummary | null>(null);
  const summaryRef = useRef<SessionEntrySummary | null>(null);

  useEffect(() => {
    if (!sessionId) return undefined;
    let active = true;
    let timer: number | undefined;

    const check = () => {
      fetchSessionEntrySummary(sessionId)
        .then((s) => {
          if (!active) return;
          summaryRef.current = s;
          setSummary(s);
          // Additive branding block on the session-entry read (item 4): pin it
          // when present; absence keeps whatever we already resolved.
          const served = parseServedBranding(s.branding);
          if (served) setServedBranding(served);
          // VC-12: the pack's customer languages ride the same read — a definitive read (joinable/preparing/ended)
          // pins them; an unreadable one keeps what we had.
          if (s.state !== 'unknown' && s.state !== 'not_found') setPackLocales(s.packLocales);
          // Light poll ONLY while the session is being prepared — the page
          // unlocks itself the moment the backend reports it joinable.
          if (s.state === 'preparing') {
            timer = window.setTimeout(check, PREPARING_POLL_MS);
          }
        })
        .catch(() => {
          // fetchSessionEntrySummary maps its own failures; this is belt-and-
          // braces so the front door can never white-screen on a read.
          if (!active) return;
          const s: SessionEntrySummary = { state: 'unknown', vcipStatus: null, branding: null, packLocales: null };
          summaryRef.current = s;
          setSummary(s);
        });
    };

    // Customers background this tab (WhatsApp → browser → back). When they
    // return mid-preparation, refresh immediately instead of waiting a cycle.
    const onVisibility = () => {
      if (
        document.visibilityState === 'visible' &&
        summaryRef.current?.state === 'preparing'
      ) {
        window.clearTimeout(timer);
        check();
      }
    };

    // 18-Aug RCA (token race): the landing's reads are session-scoped — seed the bearer FIRST and
    // chain the read behind it (request() also awaits any in-flight exchange as a belt). A refused
    // exchange still runs the read: it maps 401 → 'unknown' → the honest state-agnostic landing.
    void exchangeSessionToken(sessionId).then(() => {
      if (active) check();
    });
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      active = false;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [sessionId]);

  // Read the frozen plan alongside the summary so the CTA can be plan-derived. Fail-closed: any failure or
  // an empty plan leaves `owesPreCall` true, which routes to /start rather than the call. The plan is never
  // legitimately empty (the regime floor guarantees rows), so "empty" means "we do not know", not "nothing
  // owed" — and we must not open the call on a guess.
  const [owesPreCall, setOwesPreCall] = useState(true);
  useEffect(() => {
    if (!sessionId) return;
    let active = true;
    fetchStartBootstrap(sessionId)
      .then((dto) => {
        if (!active) return;
        setOwesPreCall(!mayEnterCall(decideEntry(dto, false)));
      })
      .catch(() => {
        if (!active) return;
        setOwesPreCall(true);
      });
    return () => {
      active = false;
    };
  }, [sessionId]);

  const brandStyle = deriveBrandTokens(tenant.branding) as unknown as CSSProperties;
  const state = sessionId ? (summary?.state ?? 'checking') : 'not_found';
  // The browser tab carries the tenant ONLY when the server named one — the
  // dev registry's fallback names never reach a customer's tab.
  useBrandedDocumentTitle(servedBranding?.name);

  return (
    <Dataroom brand={tenant.id} style={brandStyle}>
      <main className="app-screen" aria-label={`${tenant.name} · Video verification`}>
        {state === 'ended' || state === 'not_found' ? (
          <TerminalScreen tenant={tenant} state={state} />
        ) : (
          <LandingScreen
            tenant={tenant}
            state={state}
            onJoin={() => navigate(`/call/${sessionId}`)}
            onStart={() => navigate(`/start/${sessionId}`)}
            sessionId={sessionId ?? ''}
            owesPreCall={owesPreCall}
          />
        )}
        {/* Owner round 2026-08-28 item 4 — the platform credit, on the front
            door and its terminal (ended / dead-link) screens alike. */}
        <PoweredByKycHub />
      </main>
    </Dataroom>
  );
}

/* ── The landing proper (checking / preparing / joinable / unknown) ─────────── */

function LandingScreen({
  tenant,
  state,
  onJoin,
  onStart,
  sessionId,
  owesPreCall,
}: {
  tenant: TenantConfig;
  state: 'checking' | 'preparing' | 'joinable' | 'unknown';
  onJoin: () => void;
  onStart: () => void;
  sessionId: string;
  /** The frozen plan still owes a customer-actionable REQUIRED PRE_CALL check. */
  owesPreCall: boolean;
}): JSX.Element {
  const { t } = useT();
  // The CTA is decided by the PLAN, not by FSM state.
  //
  // It used to be decided by state alone: `preparing` → start, everything else → join. That is why the
  // pre-call rail vanished — the moment an officer warmed a session to READY the customer was sent straight
  // to `/call`, and `/call` never read the plan. FSM state answers "may the customer join?"; only the frozen
  // plan answers "has the customer done what the regime requires first?".
  //
  // `unknown` (the read failed) routes to /start, NOT to the call: /start is fail-closed, so an
  // unreadable session cannot silently skip verification. Server-side enforcement remains the backstop, but
  // the front door must not be the thing that opens the bypass.
  //
  // The decision is PURE ({@link landingCta}) because its previous inline form shipped a dead front door
  // (2026-08-01 preview P0): `disabled={!canJoin && state !== 'preparing'}` disabled the START button the
  // moment a session warmed to READY while the customer still owed the pre-call rail — which is the NORMAL
  // arrival state — so no customer could begin verification at all.
  const cta = landingCta(state, owesPreCall);
  const primaryAction = cta.target === 'call' ? onJoin : onStart;

  return (
    <div className="app-view">
      <AppBar
        tenant={tenant}
        subtitle={t('droom.landing.subtitle')}
        right={
          state === 'preparing' ? (
            <SecurePill kind="queue" label={t('droom.pill.preparing')} />
          ) : undefined
        }
      />

      <div className="stack">
        <LocaleSwitcher />
        <div>
          <div className="h-title">{t('droom.landing.title')}</div>
          <p className="sub-sm" style={{ marginTop: 8 }}>
            {t('droom.landing.sub')}
          </p>
        </div>

        <div className="card notice-summary">
          <div className="notice-head">
            <span style={{ fontWeight: 700, fontSize: 'var(--fs-cap)' }}>
              {t('droom.landing.need.head')}
            </span>
          </div>
          <NeedRow icon="doc" title={t('droom.landing.need.id.t')} detail={t('droom.landing.need.id.d')} />
          <NeedRow icon="camera" title={t('droom.landing.need.cam.t')} detail={t('droom.landing.need.cam.d')} />
          <NeedRow icon="mappin" title={t('droom.landing.need.loc.t')} detail={t('droom.landing.need.loc.d')} />
          <NeedRow icon="face" title={t('droom.landing.need.place.t')} detail={t('droom.landing.need.place.d')} />
        </div>

        <div className="card notice-summary">
          <div className="notice-head">
            <span style={{ fontWeight: 700, fontSize: 'var(--fs-cap)' }}>
              {t('droom.landing.happen.head')}
            </span>
          </div>
          <div className="pad" style={{ paddingTop: 12 }}>
            <p className="sub-sm">{rich(t('droom.landing.happen.p1'))}</p>
            <p className="sub-sm" style={{ marginTop: 10 }}>
              {t('droom.landing.happen.p2')}
            </p>
          </div>
        </div>

        {/* Session-state band — only ever claims what the backend reported. */}
        {state === 'checking' ? (
          <StatusBand tone="muted" icon="autoconnect" title={t('droom.landing.band.checking.t')}>
            {t('droom.landing.band.checking.b')}
          </StatusBand>
        ) : null}
        {state === 'preparing' ? (
          <StatusBand tone="wait" icon="clock" title={t('droom.landing.band.preparing.t')}>
            {t('droom.landing.band.preparing.b')}
          </StatusBand>
        ) : null}
        {state === 'joinable' ? (
          <StatusBand tone="success" icon="shield-check" title={t('droom.landing.band.joinable.t')}>
            {t('droom.landing.band.joinable.b')}
          </StatusBand>
        ) : null}
      </div>

      <div className="spacer" />

      <div className="stack-sm" style={{ marginTop: 16 }}>
        <Button onClick={primaryAction} disabled={!cta.enabled}>
          {t(cta.labelKey)}
        </Button>
        {sessionId ? (
          <div style={{ textAlign: 'center' }}>
            <span className="ref-chip">
              <span>{t('droom.landing.session')}</span>
              <b className="mono">{shortRef(sessionId)}</b>
            </span>
          </div>
        ) : null}
        <FooterNote />
      </div>
    </div>
  );
}

/* ── Terminal screens (ended / not found) — focused, no dead CTA ────────────── */

function TerminalScreen({
  tenant,
  state,
}: {
  tenant: TenantConfig;
  state: 'ended' | 'not_found';
}): JSX.Element {
  const { t } = useT();
  const ended = state === 'ended';
  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={t('droom.landing.subtitle')} />

      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className="center-col" style={{ maxWidth: '32ch' }}>
          <span
            style={{
              display: 'inline-flex',
              padding: 14,
              borderRadius: '50%',
              background: 'var(--surface-elevated)',
              color: 'var(--text-secondary)',
            }}
          >
            <Icon name={ended ? 'clock' : 'shield'} size={30} />
          </span>
          <div className="h-title sm">
            {ended ? t('droom.landing.ended.t') : t('droom.landing.notfound.t')}
          </div>
          <p className="sub" style={{ marginTop: 4 }}>
            {ended ? t('droom.landing.ended.b') : t('droom.landing.notfound.b')}
          </p>
        </div>
      </div>

      <div className="stack-sm">
        <FooterNote />
      </div>
    </div>
  );
}

/* ── Small pieces ───────────────────────────────────────────────────────────── */

function NeedRow({
  icon,
  title,
  detail,
}: {
  icon: IconName;
  title: string;
  detail: string;
}): JSX.Element {
  return (
    <div className="purpose">
      <span className="pic">
        <Icon name={icon} size={16} />
      </span>
      <div>
        <div className="pt">{title}</div>
        <div className="pd">{detail}</div>
      </div>
    </div>
  );
}

const BAND_TONES = {
  muted: { fg: 'var(--text-secondary)', bg: 'var(--surface-elevated)' },
  wait: { fg: 'var(--wait)', bg: 'var(--wait-subtle)' },
  success: { fg: 'var(--success)', bg: 'var(--success-subtle)' },
} as const;

/** One-line session-state banner. Token-toned; announced politely to AT. */
function StatusBand({
  tone,
  icon,
  title,
  children,
}: {
  tone: keyof typeof BAND_TONES;
  icon: IconName;
  title: string;
  children: ReactNode;
}): JSX.Element {
  const t = BAND_TONES[tone];
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 11,
        padding: '12px 14px',
        borderRadius: 'var(--r-xl)',
        background: t.bg,
      }}
    >
      <span style={{ color: t.fg, display: 'grid', placeItems: 'center', marginTop: 1 }}>
        <Icon name={icon} size={18} />
      </span>
      <div>
        <div style={{ fontWeight: 700, fontSize: 'var(--fs-cap)', color: t.fg }}>
          {title}
        </div>
        <div className="sub-sm" style={{ marginTop: 2 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

/** Capability-URL honesty — the link IS the access credential (interim model). */
function FooterNote(): JSX.Element {
  const { t } = useT();
  return (
    <div className="cobrand" style={{ padding: '0 8px' }}>
      {t('droom.landing.footer')}
    </div>
  );
}

/** Short display handle for support conversations — never the full capability id. */
function shortRef(sessionId: string): string {
  return sessionId.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase();
}
